import { useEffect, useRef, type ReactNode } from "react";
import { motion, useInView, useReducedMotion } from "motion/react";
import { animate, stagger } from "animejs";

const SILK: [number, number, number, number] = [0.22, 1, 0.36, 1];

/* ------------------------------------------------------------------------- */
/* Ornament — a hairline rule that draws itself around a small glass flower. */
/* ------------------------------------------------------------------------- */
export function Ornament({ className = "" }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const reduce = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el || !inView) return;

    const lines = el.querySelectorAll<HTMLElement>("[data-orn-line]");
    const flower = el.querySelector<HTMLElement>("[data-orn-flower]");

    if (reduce) {
      lines.forEach((line) => {
        line.style.transform = "scaleX(1)";
      });
      if (flower) flower.style.opacity = "1";
      return;
    }

    animate(Array.from(lines), {
      scaleX: [0, 1],
      duration: 1200,
      ease: "outQuint",
      delay: stagger(120),
    });
    if (flower) {
      flower.style.opacity = "1";
      animate(flower, {
        scale: [0.2, 1],
        rotate: [-90, 0],
        duration: 1100,
        ease: "outBack",
        delay: 240,
      });
    }
  }, [inView, reduce]);

  return (
    <div ref={ref} className={`flex items-center gap-4 ${className}`} aria-hidden="true">
      <span
        data-orn-line
        className="h-px flex-1 origin-left bg-gradient-to-r from-transparent via-white/20 to-white/35"
        style={reduce ? undefined : { transform: "scaleX(0)" }}
      />
      <span
        data-orn-flower
        className="grid shrink-0 place-items-center"
        style={reduce ? undefined : { opacity: 0 }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <defs>
            <linearGradient id="petal" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f6c9dd" />
              <stop offset="55%" stopColor="#cdb4f7" />
              <stop offset="100%" stopColor="#9dc6ff" />
            </linearGradient>
          </defs>
          {[0, 90, 180, 270].map((angle) => (
            <ellipse
              key={angle}
              cx="12"
              cy="6.4"
              rx="2.7"
              ry="4.9"
              fill="url(#petal)"
              opacity="0.75"
              transform={`rotate(${angle} 12 12)`}
            />
          ))}
          <circle cx="12" cy="12" r="1.8" fill="#fff6fb" opacity="0.9" />
        </svg>
      </span>
      <span
        data-orn-line
        className="h-px flex-1 origin-right bg-gradient-to-l from-transparent via-white/20 to-white/35"
        style={reduce ? undefined : { transform: "scaleX(0)" }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* MaskText — an editorial line that rises out of a clipped box.             */
/* ------------------------------------------------------------------------- */
export function MaskText({
  children,
  delay = 0,
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();

  return (
    <span className={`mask-line ${className}`}>
      <motion.span
        initial={reduce ? { y: "0%" } : { y: "118%", rotate: 2.5 }}
        whileInView={{ y: "0%", rotate: 0 }}
        viewport={{ once: true, margin: "-8% 0px" }}
        transition={{ duration: 1, delay, ease: SILK }}
      >
        {children}
      </motion.span>
    </span>
  );
}

/* ------------------------------------------------------------------------- */
/* Reveal — glass condensing into focus rather than a plain slide-up.         */
/* ------------------------------------------------------------------------- */
export function Reveal({
  children,
  delay = 0,
  y = 26,
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y, filter: "blur(16px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-10% 0px" }}
      transition={{ duration: 0.9, delay, ease: SILK }}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------------- */
/* TagList — small glass chips that settle in, staggered, via anime.js.      */
/* ------------------------------------------------------------------------- */
export function TagList({ items, className = "" }: { items: string[]; className?: string }) {
  const ref = useRef<HTMLUListElement>(null);
  const inView = useInView(ref, { once: true, margin: "-8% 0px" });
  const reduce = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el || !inView || reduce) return;
    const chips = el.querySelectorAll<HTMLElement>("[data-chip]");
    animate(Array.from(chips), {
      opacity: [0, 1],
      translateY: [10, 0],
      scale: [0.92, 1],
      duration: 640,
      ease: "outCubic",
      delay: stagger(65),
    });
  }, [inView, reduce]);

  return (
    <ul ref={ref} className={`flex flex-wrap gap-2 ${className}`}>
      {items.map((item) => (
        <li
          key={item}
          data-chip
          style={reduce ? undefined : { opacity: 0 }}
          className="pill bg-white/[0.055] px-3 py-1 text-[0.78rem] font-medium text-mist shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] backdrop-blur-sm"
        >
          {item}
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------------- */
/* Section — the page's repeating rhythm.                                     */
/* ------------------------------------------------------------------------- */
export function Section({
  id,
  title,
  intro,
  children,
  className = "",
}: {
  id: string;
  title: string;
  intro?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className={`scroll-mt-28 py-20 sm:py-28 ${className}`}>
      <header className="mb-12">
        <h2 className="text-display ink-gradient text-[clamp(2.1rem,5.2vw,3.5rem)] leading-[1.02] font-medium">
          <MaskText>{title}</MaskText>
        </h2>
        {intro ? (
          <Reveal delay={0.15}>
            <p className="mt-4 max-w-xl text-[1.02rem] text-mist">{intro}</p>
          </Reveal>
        ) : null}
        <Ornament className="mt-8" />
      </header>
      {children}
    </section>
  );
}
